import { useState } from "react";
import { toast } from "react-toastify";

import {
  beginMfaEnrolment,
  completeMfaEnrolment,
  disableMfa,
} from "../../../utils/apis/func";

/**
 * §22.2 — enrol this admin account in TOTP.
 *
 * Enrolment is deliberately two steps, and this screen makes that visible:
 * asking for the secret does NOT turn the second factor on. Only entering a code
 * from the app does. An admin whose authenticator failed to scan can close this
 * page and still log in, which is the whole reason the backend works that way.
 *
 * The backup codes are shown exactly once, on completion, because only their
 * hashes are stored. That is stated on screen rather than assumed, since the
 * alternative to writing them down is editing the database by hand.
 *
 * No QR image library is pulled in for this: the otpauth URI is shown as text
 * that can be pasted into any authenticator app, which every app supports and
 * which avoids shipping a rendering dependency for one screen.
 */
const TwoFactor = () => {
  const [otpauthUrl, setOtpauthUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const fail = (m: string) =>
    toast(m, { position: "top-right", autoClose: 5000, theme: "light", type: "error" });
  const ok = (m: string) =>
    toast(m, { position: "top-right", autoClose: 5000, theme: "light" });

  const begin = async () => {
    setBusy(true);
    const res: any = await beginMfaEnrolment();
    setBusy(false);
    if (res?.status === 200) {
      setOtpauthUrl(res.data.otpauthUrl);
      setSecret(res.data.secret);
      return;
    }
    fail(res?.error?.message || "Could not start setup");
  };

  const complete = async () => {
    const cleaned = code.replace(/\s+/g, "");
    if (cleaned.length !== 6) return fail("Enter the 6 digit code from your app");
    setBusy(true);
    const res: any = await completeMfaEnrolment(cleaned);
    setBusy(false);
    if (res?.status === 200) {
      setBackupCodes(res.data.backupCodes || []);
      setOtpauthUrl("");
      setSecret("");
      setCode("");
      ok("Two factor is on for your account");
      return;
    }
    setCode("");
    fail(res?.error?.message || "That code is not valid");
  };

  const turnOff = async () => {
    const cleaned = code.replace(/\s+/g, "");
    if (!cleaned) return fail("Enter a current code to turn it off");
    setBusy(true);
    const res: any = await disableMfa(cleaned);
    setBusy(false);
    if (res?.status === 200) {
      setCode("");
      ok("Two factor turned off");
      return;
    }
    fail(res?.error?.message || "That code is not valid");
  };

  const input = (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={11}
      aria-label="Authentication code"
      value={code}
      onChange={(e) => setCode(e.target.value)}
      placeholder="123456"
      className="w-[220px] py-2 px-4 text-center tracking-[0.3em] bg-[#EFF6FF] rounded-md outline-none"
    />
  );

  return (
    <div className="px-1 lg:px-8 max-w-2xl">
      <h2 className="font-outfit text-lg font-semibold mb-2">
        Two factor authentication
      </h2>
      <p className="text-sm text-[#6B7280] mb-6">
        A password on its own is all that stands between someone and every
        customer record. With this on, signing in also needs a code from your
        phone.
      </p>

      {backupCodes.length > 0 && (
        <div className="mb-8 p-4 border border-[#88087B] rounded-md">
          <p className="font-medium mb-1">Save these backup codes now</p>
          <p className="text-sm text-[#6B7280] mb-3">
            They are shown once and never again. Each one works a single time,
            and they are the only way back in if you lose your phone.
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((c) => (
              <span key={c} className="bg-[#F7F7F7] py-1 px-2 rounded">{c}</span>
            ))}
          </div>
        </div>
      )}

      {!otpauthUrl && backupCodes.length === 0 && (
        <button
          onClick={begin}
          disabled={busy}
          className="bg-[#000413] text-[#FFC727] font-medium py-2 px-6 rounded-md disabled:opacity-60"
        >
          {busy ? "Please wait..." : "Set up two factor"}
        </button>
      )}

      {otpauthUrl && (
        <div className="space-y-4">
          <p className="text-sm">
            1. Add this to your authenticator app (Google Authenticator, 1Password,
            Authy). Most apps let you paste a setup link:
          </p>
          <textarea
            readOnly
            value={otpauthUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full h-20 text-xs font-mono p-2 bg-[#F7F7F7] rounded-md"
          />
          <p className="text-sm">
            Or type the key by hand: <span className="font-mono">{secret}</span>
          </p>
          <p className="text-sm">2. Then enter the code it shows:</p>
          <div className="flex gap-3 items-center">
            {input}
            <button
              onClick={complete}
              disabled={busy}
              className="bg-[#000413] text-[#FFC727] font-medium py-2 px-6 rounded-md disabled:opacity-60"
            >
              {busy ? "Checking..." : "Turn on"}
            </button>
          </div>
          <p className="text-xs text-[#6B7280]">
            Nothing changes until you enter a valid code, so you cannot lock
            yourself out by leaving this page.
          </p>
        </div>
      )}

      <div className="mt-10 pt-6 border-t">
        <p className="font-medium mb-1">Turn two factor off</p>
        <p className="text-sm text-[#6B7280] mb-3">
          Needs a current code or a backup code, so that someone using your
          signed-in computer cannot remove it.
        </p>
        <div className="flex gap-3 items-center">
          {input}
          <button
            onClick={turnOff}
            disabled={busy}
            className="border border-[#000413] font-medium py-2 px-6 rounded-md disabled:opacity-60"
          >
            Turn off
          </button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactor;
